<div class="settings-container">
  <div class="settings-header">
    <h1>Settings</h1>
    <button class="back-button" (click)="goBack()">
      ← Back to Chat
    </button>
  </div>

  <form class="settings-form" #settingsForm="ngForm">
    <div class="form-section">
      <h2>LLM Configuration</h2>
      
      <div class="form-group">
        <label for="mainApiUrl">Main model API</label>
        <input 
          type="url" 
          id="mainApiUrl"
          name="mainApiUrl"
          [(ngModel)]="mainApiUrl"
          (ngModelChange)="onApiChange(); persistSettings()"
          class="form-input"
          placeholder="http://127.0.0.1:1234/"
        >
        @if (validationMessages.mainApiUrl) {
          <div class="error-message">
            {{ validationMessages.mainApiUrl }}
          </div>
        }
          <div class="default-hint">
            Default: <code>{{ defaultSettings.mainApiUrl }}</code>
          </div>
      </div>

      <div class="form-group">
        <label for="openAiApiKey">OpenAI API key <span class="default-hint">(optional)</span></label>
        <input
          type="password"
          id="openAiApiKey"
          name="openAiApiKey"
          [(ngModel)]="openAiApiKey"
          class="form-input"
          autocomplete="new-password"
          placeholder="sk-..."
        >
        <div class="key-actions">
          <button type="button" class="restore-btn" (click)="saveOpenAiKey()" [disabled]="savingApiKey || !openAiApiKey.trim()">
            {{ apiKeyConfigured ? 'Replace saved key' : 'Save key securely' }}
          </button>
          @if (settingsSync.openAiKeyConfigured()) {
            <button type="button" class="restore-btn" (click)="removeOpenAiKey()" [disabled]="savingApiKey">Remove saved key</button>
          }
        </div>
        <p class="default-hint">{{ apiKeyConfigured ? 'A key is configured. Saved keys are encrypted and are never shown again.' : 'No key is saved. LM Studio does not require one.' }}</p>
        @if (apiKeyStatus) { <p class="status-message">{{ apiKeyStatus }}</p> }
      </div>

      <div class="form-group">
        <label for="model">Model</label>
        <select
          id="model"
          name="model"
          [(ngModel)]="model"
          (ngModelChange)="persistSettings()"
          class="form-input"
          [disabled]="loadingModels"
        >
          <option value="">— Use default —</option>
          @for (m of modelList; track m) {
            <option [value]="m">{{ m }}</option>
          }
          @if (model && !modelList.includes(model)) {
            <option [value]="model">{{ model }} (current)</option>
          }
        </select>
        @if (loadingModels) {
          <div class="status-message">Loading models…</div>
        }
        @if (modelListError) {
          <div class="error-message">{{ modelListError }}</div>
        }
          <div class="default-hint">
            Default: <code>{{ defaultSettings.model }}</code>
          </div>
      </div>

    </div>

    <div class="form-section">
      <h2>Generation Settings</h2>
      
      <div class="form-group">
        <label for="temperature">Temperature: {{ temperature }}</label>
        <input 
          type="range" 
          id="temperature"
          name="temperature"
          [(ngModel)]="temperature"
          (ngModelChange)="persistSettings()"
          min="0" 
          max="1" 
          step="0.01"
          class="form-slider"
        >
        <div class="slider-values">
          <span>0 (Precise)</span>
          <span>1 (Creative)</span>
        </div>
          <div class="default-hint">
            Default: <code>{{ defaultSettings.temperature }}</code>
          </div>
      </div>

      <div class="form-group">
        <label>Reasoning Effort</label>
        <div class="radio-group">
          <label class="radio-label">
            <input 
              type="radio" 
              name="reasoningEffort"
              [(ngModel)]="reasoningEffort"
              (ngModelChange)="persistSettings()"
              value="low"
              class="radio-input"
            >
            Low
          </label>
          <label class="radio-label">
            <input 
              type="radio" 
              name="reasoningEffort"
              [(ngModel)]="reasoningEffort"
              (ngModelChange)="persistSettings()"
              value="medium"
              class="radio-input"
            >
            Medium
          </label>
          <label class="radio-label">
            <input 
              type="radio" 
              name="reasoningEffort"
              [(ngModel)]="reasoningEffort"
              (ngModelChange)="persistSettings()"
              value="high"
              class="radio-input"
            >
            High
          </label>
        </div>
        <div class="default-hint">
          Default: <code>{{ defaultSettings.reasoningEffort }}</code>
        </div>
      </div>

      <label class="toggle-label" for="relatedTopicGenerationEnabled">
        <input
          id="relatedTopicGenerationEnabled"
          name="relatedTopicGenerationEnabled"
          type="checkbox"
          [(ngModel)]="relatedTopicGenerationEnabled"
          (ngModelChange)="persistSettings()"
        >
        <span>Generate related topics after responses</span>
      </label>
      <p class="default-hint">Suggests follow-up questions based on the completed conversation.</p>
    </div>

    <div class="form-section">
      <h2>Helper Model</h2>
      <label class="toggle-label" for="helperModelEnabled">
        <input
          id="helperModelEnabled"
          name="helperModelEnabled"
          type="checkbox"
          [(ngModel)]="helperModelEnabled"
          (ngModelChange)="onHelperEnabledChange()"
        >
        <span>Enable Helper Model</span>
      </label>

      @if (helperModelEnabled) {
        <div class="form-group">
          <label for="helperApiUrl">Helper model API <span class="default-hint">(optional)</span></label>
          <input type="url" id="helperApiUrl" name="helperApiUrl" [(ngModel)]="helperApiUrl" (ngModelChange)="onHelperApiChange(); persistSettings()" class="form-input" placeholder="Leave empty to use the main model API">
          @if (validationMessages.helperApiUrl) { <div class="error-message">{{ validationMessages.helperApiUrl }}</div> }
        </div>
        <div class="form-group">
          <label for="helperModelId">Model</label>
          <select
            id="helperModelId"
            name="helperModelId"
            [(ngModel)]="helperModelId"
            (ngModelChange)="persistSettings()"
            class="form-input"
            [disabled]="loadingHelperModels"
          >
            <option value="">â€” Select a model â€”</option>
            @for (m of helperModelList; track m) {
              <option [value]="m">{{ m }}</option>
            }
            @if (helperModelId && !helperModelList.includes(helperModelId)) {
              <option [value]="helperModelId">{{ helperModelId }} (unavailable)</option>
            }
          </select>
        @if (loadingHelperModels) {
            <div class="status-message">Loading modelsâ€¦</div>
          }
          @if (modelListError) {
            <div class="error-message">{{ modelListError }}</div>
          }
        </div>
        <p class="default-hint">Uses a secondary model for query analysis, search preparation and other lightweight tasks. If no helper API is set, it uses the main API.</p>
      }
    </div>

    <div class="form-section">
      <h2>Diagnostics</h2>
      <label class="toggle-label" for="logLlmRequests">
        <input
          id="logLlmRequests"
          name="logLlmRequests"
          type="checkbox"
          [(ngModel)]="logLlmRequests"
          (ngModelChange)="persistSettings()"
        >
        <span>Log LLM requests in the browser console</span>
      </label>
      <p class="default-hint">Includes prompts, RAG/web context, endpoint, model, and response metadata. Keep this off when someone else can inspect the browser console.</p>
    </div>

    <div class="form-section">
      <h2>Actions</h2>
      
      <button 
        type="button" 
        class="clear-history-button"
        (click)="clearChatHistory()"
      >
        Clear Chat History
      </button>
      <button type="button" class="restore-btn" (click)="restoreDefaults()">
        Restore to Defaults
      </button>
    </div>
  </form>
</div>
